import React, { useMemo, useState, useRef } from 'react';
import { useIntl } from 'react-intl';
import { useAtomValue, useSetAtom } from 'jotai';
import { useDebounce } from 'use-debounce';
import {
  Box,
  Typography,
  Divider,
  TextField,
  Stack,
  Button,
  Tooltip,
  IconButton,
  Chip,
} from '@mui/material';
import {
  FullSizeColumn,
  CenteredFlexBox,
} from '../../../../../Components/FlexBox';
import SaveOutlinedIcon from '@mui/icons-material/SaveOutlined';
import CodeRoundedIcon from '@mui/icons-material/CodeRounded';
import ContentCopyRoundedIcon from '@mui/icons-material/ContentCopyRounded';
import FileUploadRoundedIcon from '@mui/icons-material/FileUploadRounded';
import RestartAltRoundedIcon from '@mui/icons-material/RestartAltRounded';
import {
  schemaJsonAtom,
  validationAtom,
  importSchemaAtom,
  resetSchemaAtom,
} from '../store/schemaAtoms';
import SchemaPaper from '../../../../../Components/engine/SchemaPaper';

const JsonPreviewPane = function JsonPreviewPane({ onSave, data }) {
  const intl = useIntl();
  const t = (id) => intl.formatMessage({ id });
  const schema = useAtomValue(schemaJsonAtom);
  const { isValid, errors } = useAtomValue(validationAtom);
  const importSchema = useSetAtom(importSchemaAtom);
  const resetSchema = useSetAtom(resetSchemaAtom);
  const fileInputRef = useRef(null);
  const [importError, setImportError] = useState(null);

  const [debouncedSchema] = useDebounce(schema, 250);
  const jsonString = useMemo(
    () => JSON.stringify(debouncedSchema, null, 2),
    [debouncedSchema]
  );

  const handleCopy = async () => {
    try {
      await navigator.clipboard.writeText(jsonString);
    } catch {
      /* noop */
    }
  };

  const handleImportClick = () => fileInputRef.current?.click();

  const handleFileSelected = async (e) => {
    const file = e.target.files?.[0];
    e.target.value = '';
    if (!file) return;
    try {
      const text = await file.text();
      const res = importSchema(text);
      if (!res.ok) setImportError(res.error || t('SCHEMA_INVALID_JSON'));
      else setImportError(null);
    } catch (err) {
      setImportError(err.message);
    }
  };

  const handleSave = () => {
    if (!isValid) return;
    onSave?.(jsonString);
  };

  return (
    <FullSizeColumn>
      <CenteredFlexBox justifyContent="space-between" padding="8px" gap="8px">
        <CodeRoundedIcon fontSize="small" sx={{ color: '#8be9fd' }} />
        <Typography
          variant="overline"
          sx={{
            fontWeight: 700,
            letterSpacing: '0.08em',
            color: '#8be9fd',
            flexGrow: 1,
          }}
        >
          {t('SCHEMA_PREVIEW_TITLE')}
        </Typography>
        <Chip
          size="small"
          label={`${(jsonString.length / 1024).toFixed(1)} KB`}
          sx={{
            ml: 'auto',
            height: 20,
            fontWeight: 600,
            backgroundColor: 'rgba(139, 233, 250, 0.14)',
            color: '#8be9fd',
            fontFamily: 'ui-monospace, monospace',
          }}
        />
        <Tooltip title={t('SCHEMA_COPY')}>
          <IconButton
            size="small"
            onClick={handleCopy}
            sx={{ color: '#8be9fd' }}
          >
            <ContentCopyRoundedIcon fontSize="inherit" />
          </IconButton>
        </Tooltip>
        <Tooltip title={t('SCHEMA_IMPORT_JSON')}>
          <IconButton
            size="small"
            onClick={handleImportClick}
            sx={{ color: '#8be9fd' }}
          >
            <FileUploadRoundedIcon fontSize="inherit" />
          </IconButton>
        </Tooltip>
        <Tooltip title={t('SCHEMA_RESET')}>
          <IconButton
            size="small"
            onClick={() => resetSchema()}
            sx={{ color: '#ff79c6' }}
          >
            <RestartAltRoundedIcon fontSize="inherit" />
          </IconButton>
        </Tooltip>
        <input
          ref={fileInputRef}
          type="file"
          accept="application/json"
          hidden
          onChange={handleFileSelected}
        />
      </CenteredFlexBox>
      <Divider />
      <Box sx={{ flexGrow: 1, overflow: 'auto', padding: '8px' }}>
        <SchemaPaper
          name={data?.name}
          description={data?.description}
          uiSchemaConfiguration={JSON.parse(jsonString)}
          active
        />
      </Box>
      {importError && (
        <Box sx={{ padding: '0 16px 8px', color: '#ff5555', fontSize: 12 }}>
          {`${t('SCHEMA_IMPORT_FAILED')}: ${importError}`}
        </Box>
      )}
      <Divider />
      <CenteredFlexBox justifyContent="flex-end" padding="8px">
        <Button
          id="schema-save-btn"
          variant="contained"
          disabled={!isValid}
          onClick={handleSave}
          startIcon={<SaveOutlinedIcon />}
        >
          {t('SCHEMA_SAVE')}
        </Button>
      </CenteredFlexBox>
    </FullSizeColumn>
  );
};

export default JsonPreviewPane;
